import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList,
  Loader2,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  Hash,
  Ruler,
  Calendar,
  Clock,
  Trash2,
  Save,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useUpdateReportType, useDeactivateReportType } from '@/features/reports/hooks';
import { triggerHapticFeedback } from '../utils/adminAnimations';
import toast from 'react-hot-toast';
import type { ReportTypeDefinition } from '@/features/reports/types';

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const actionBtnStyle = {
  border: `1px solid ${MODAL_COLORS.border}`,
};

const inputStyle = {
  backgroundColor: MODAL_COLORS.surface,
  border: `1px solid ${MODAL_COLORS.border}`,
  color: MODAL_COLORS.text,
};

const UNIT_OPTIONS = ['per_unit', 'per_hour', 'per_day', 'per_week'] as const;

interface ReportTypeDetailModalProps {
  reportType: ReportTypeDefinition;
  onClose: () => void;
}

export function ReportTypeDetailModal({ reportType, onClose }: ReportTypeDetailModalProps) {
  const { t } = useTranslation();
  const { openConfirm } = useModal();
  const updateMutation = useUpdateReportType();
  const deleteMutation = useDeactivateReportType();
  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  // Editable fields
  const [name, setName] = useState(reportType.name);
  const [nameEn, setNameEn] = useState(reportType.nameEn ?? '');
  const [description, setDescription] = useState(reportType.description ?? '');
  const [unit, setUnit] = useState(reportType.unit ?? 'per_unit');
  const [quantityAlias, setQuantityAlias] = useState(reportType.quantityAlias ?? '');
  const [quantityAliasEn, setQuantityAliasEn] = useState(reportType.quantityAliasEn ?? '');

  const hasChanges =
    name !== reportType.name ||
    nameEn !== (reportType.nameEn ?? '') ||
    description !== (reportType.description ?? '') ||
    unit !== (reportType.unit ?? 'per_unit') ||
    quantityAlias !== (reportType.quantityAlias ?? '') ||
    quantityAliasEn !== (reportType.quantityAliasEn ?? '');

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      triggerHapticFeedback();
      await updateMutation.mutateAsync({
        id: reportType.id,
        data: {
          name: name.trim(),
          nameEn: nameEn.trim() || undefined,
          description: description.trim() || undefined,
          quantityAlias: quantityAlias.trim() || undefined,
          quantityAliasEn: quantityAliasEn.trim() || undefined,
          unit,
        },
      });
      toast.success(t('reportTypesAdmin.activateSuccess'));
      requestClose();
    } catch {
      toast.error(t('reportTypesAdmin.updateFailed'));
    }
  };

  const handleToggleActive = async () => {
    try {
      triggerHapticFeedback();
      await updateMutation.mutateAsync({
        id: reportType.id,
        data: { isActive: !reportType.isActive },
      });
      toast.success(
        t(reportType.isActive ? 'reportTypesAdmin.deactivateSuccess' : 'reportTypesAdmin.activateSuccess')
      );
      requestClose();
    } catch {
      toast.error(t('reportTypesAdmin.updateFailed'));
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={reportType.name}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Status */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 opacity-60" />
              <span className="text-sm font-bold">{t('reportTypesAdmin.status')}</span>
              <span className="text-sm font-semibold opacity-70">
                {reportType.isActive ? t('reportTypesAdmin.active') : t('reportTypesAdmin.inactive')}
              </span>
            </div>
            <button
              onClick={handleToggleActive}
              disabled={updateMutation.isPending}
              className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
              style={actionBtnStyle}
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : reportType.isActive ? (
                <>
                  <ToggleRight className="w-3.5 h-3.5" />
                  {t('reportTypesAdmin.inactive')}
                </>
              ) : (
                <>
                  <ToggleLeft className="w-3.5 h-3.5" />
                  {t('reportTypesAdmin.active')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Code (read-only) */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center gap-2 text-sm">
            <Hash className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('reportTypesAdmin.code')}:</span>
            <span className="opacity-70 font-mono">{reportType.code}</span>
          </div>
        </div>

        {/* Editable fields */}
        <div className="rounded-xl p-3 space-y-2.5" style={cardStyle}>
          {/* Name */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <ClipboardList className="w-3.5 h-3.5" />
              {t('reportTypesAdmin.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Name (English) */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <ClipboardList className="w-3.5 h-3.5" />
              {t('reportTypesAdmin.nameEn')}
            </label>
            <input
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Unit */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <Ruler className="w-3.5 h-3.5" />
              {t('reportTypesAdmin.unit')}
            </label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            >
              {UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>
                  {t(`reportTypesAdmin.unit_${u}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <ClipboardList className="w-3.5 h-3.5" />
              {t('reportTypesAdmin.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Quantity Alias */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
                <ClipboardList className="w-3.5 h-3.5" />
                {t('reportTypesAdmin.quantityAlias')}
              </label>
              <input
                type="text"
                value={quantityAlias}
                onChange={(e) => setQuantityAlias(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                style={inputStyle}
              />
            </div>
            <div className="space-y-1">
              <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
                <ClipboardList className="w-3.5 h-3.5" />
                {t('reportTypesAdmin.quantityAliasEn')}
              </label>
              <input
                type="text"
                value={quantityAliasEn}
                onChange={(e) => setQuantityAliasEn(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Save button */}
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending || !name.trim()}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold transition-transform cursor-pointer disabled:opacity-50"
            style={{
              backgroundColor: MODAL_COLORS.header,
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#fff',
            }}
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('common.save')}
          </button>
        )}

        {/* Dates */}
        <div className="rounded-xl p-3 space-y-1" style={cardStyle}>
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('reportTypesAdmin.createdAt')}:</span>
            <span className="opacity-70">{new Date(reportType.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('reportTypesAdmin.updatedAt')}:</span>
            <span className="opacity-70">{new Date(reportType.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Delete */}
        <button
          onClick={() => {
            triggerHapticFeedback();
            openConfirm({
              title: t('reportTypesAdmin.delete'),
              message: t('reportTypesAdmin.deleteConfirm'),
              variant: 'danger',
              confirmLabel: t('common.delete'),
              onConfirm: async () => {
                await deleteMutation.mutateAsync(reportType.id);
                toast.success(t('reportTypesAdmin.deleteSuccess'));
                requestClose();
              },
            });
          }}
          disabled={deleteMutation.isPending}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
          style={{
            backgroundColor: 'rgba(254, 226, 226, 0.6)',
            border: `1px solid ${MODAL_COLORS.border}`,
            color: '#B91C1C',
          }}
        >
          <Trash2 className="w-4 h-4" />
          {t('reportTypesAdmin.delete')}
        </button>
      </div>
    </FormModal>
  );
}
